import React, { useEffect, useState } from "react";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Check,
  DirectionsBike,
  Inventory,
  LocalShipping,
  Restaurant,
  Schedule as ScheduleIcon,
  ShoppingBag,
  Store,
  Timeline as TimelineIcon,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import { ordersEventsTimeline } from "../../../api/order";

const HorizontalOrderTimelineCard = ({ startDate, endDate, period }) => {
  const theme = useTheme();
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const iconMap = {
    ShoppingBag,
    Check,
    RestaurantMenu: Restaurant,
    Inventory,
    DirectionsBike,
    LocalShipping,
    Store,
  };

  useEffect(() => {
    const fetchTimelineData = async () => {
      if (!startDate || !endDate) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
        });

        if (period) {
          params.append("period", period);
        }

        const response = await ordersEventsTimeline(params);
        const data = await response.data;
        setTimelineData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTimelineData();
  }, [startDate, endDate, period]);

  const formatTotalTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    const result = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    return result;
  };

  const renderTrendIndicator = (trend) => {
    if (trend === null || trend === undefined) {
      return null;
    }

    const isPositive = trend >= 0;
    const TrendIcon = isPositive ? TrendingUp : TrendingDown;
    const color = isPositive ? "error" : "success";

    return (
      <Chip
        icon={<TrendIcon />}
        label={`${Math.abs(trend).toFixed(1)}%`}
        size="small"
        color={color}
        variant="outlined"
        sx={{ ml: 1, height: 20 }}
      />
    );
  };

  const renderTimeConnector = (time, trend, isLast) => {
    if (isLast) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: 120,
          mx: 2,
        }}
      >
        <Box
          sx={{
            height: 2,
            width: "100%",
            background: `linear-gradient(90deg, ${
              theme.palette.primary.main
            } 0%, ${alpha(theme.palette.primary.main, 0.3)} 100%)`,
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              right: -4,
              top: -2,
              width: 0,
              height: 0,
              borderLeft: `6px solid ${theme.palette.primary.main}`,
              borderTop: "3px solid transparent",
              borderBottom: "3px solid transparent",
            },
          }}
        />
        <Paper
          elevation={2}
          sx={{
            mt: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <ScheduleIcon
            sx={{
              fontSize: 14,
              mr: 0.5,
              color: theme.palette.primary.main,
            }}
          />
          <Typography variant="caption" fontWeight={600} color="primary">
            {time}m
          </Typography>
          {renderTrendIndicator(trend)}
        </Paper>
      </Box>
    );
  };

  const renderEventCard = (event, index) => {
    const IconComponent = iconMap[event.icon];

    return (
      <Paper
        key={`${event.code}-${period || "custom"}`}
        elevation={3}
        sx={{
          p: 2,
          minWidth: 180,
          borderRadius: 3,
          backgroundColor: alpha(
            event.iconBg || theme.palette.primary.main,
            0.02
          ),
          border: `1px solid ${alpha(
            event.iconBg || theme.palette.primary.main,
            0.1
          )}`,
          transition: "all 0.3s ease",
          "&:hover": {
            elevation: 6,
            transform: "translateY(-2px)",
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: event.iconBg || theme.palette.primary.main,
              border: `3px solid ${alpha(
                event.iconBg || theme.palette.primary.main,
                0.2
              )}`,
            }}
          >
            {IconComponent && (
              <IconComponent
                sx={{
                  color: event.iconColor || theme.palette.primary.contrastText,
                  fontSize: 24,
                }}
              />
            )}
          </Avatar>

          <Stack spacing={0.5} alignItems="center">
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.primary"
              textAlign="center"
            >
              {event.full_code}
            </Typography>

            <Chip
              label={event.code}
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.7rem",
                height: 20,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minHeight: 24,
                lineHeight: 1.2,
              }}
            >
              {event.description}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderLoadingState = () => {
    return (
      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardHeader
          avatar={<TimelineIcon />}
          title="Jornada do Pedido"
          sx={{ backgroundColor: theme.palette.primary.main }}
          slotProps={{
            title: { sx: { color: "#ffffff" } },
            avatar: { sx: { color: "#ffffff" } },
          }}
        />
        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderErrorState = () => {
    return (
      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardHeader
          avatar={<TimelineIcon />}
          title="Jornada do Pedido"
          sx={{ backgroundColor: theme.palette.primary.main, color: "white" }}
        />
        <CardContent sx={{ p: 3 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Erro ao carregar dados da jornada do pedido: {error}
          </Alert>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return renderLoadingState();
  }

  if (error) {
    return renderErrorState();
  }

  if (!timelineData) {
    return null;
  }

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.02
        )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
            <TimelineIcon />
          </Avatar>
        }
        title={
          <Typography variant="h6" fontWeight={700}>
            Jornada do Pedido
          </Typography>
        }
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "white",
          "& .MuiCardHeader-avatar": {
            mr: 2,
          },
        }}
      />

      <CardContent sx={{ p: 3 }}>
        <Paper
          elevation={2}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                backgroundColor: theme.palette.primary.main,
                width: 48,
                height: 48,
              }}
            >
              <ScheduleIcon />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
                sx={{ display: "block" }}
              >
                Tempo Médio Total do Pedido
              </Typography>
              <Typography variant="h5" fontWeight={700} color="primary">
                {formatTotalTime(timelineData.totalTime)}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Box
          sx={{
            overflowX: "auto",
            pb: 2,
            "&::-webkit-scrollbar": {
              height: 8,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: alpha(theme.palette.grey[300], 0.3),
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.primary.main, 0.5),
              borderRadius: 4,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.7),
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "max-content",
              py: 2,
            }}
          >
            {timelineData.timeline.map((event, index) => {
              const isLast = index === timelineData.timeline.length - 1;

              return (
                <React.Fragment key={`fragment-${event.code}-${index}`}>
                  {renderEventCard(event, index)}
                  {renderTimeConnector(
                    event.avgTimeToNext,
                    event.trend,
                    isLast
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction="row"
          justifyContent="center"
          spacing={3}
          flexWrap="wrap"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: theme.palette.success.main,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Mais rápido que a média
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: theme.palette.error.main,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Mais lento que a média
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default HorizontalOrderTimelineCard;
